import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Stack, Divider,
  CircularProgress, Alert,
} from '@mui/material';
import { CheckCircle, Close, PrintOutlined, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  useCreateExchangeMutation,
  useCompleteExchangeMutation,
} from '../../services/exchangeApi';
import { useToast } from '../common/ToastProvider';
import { usePrinterContext, browserPrintExchange } from '../../context/PrinterContext';
import { mapExchangeToReceipt } from './receipt/mapExchangeToReceipt';
import { useGetReceiptTemplateQuery } from '../../services/receiptTemplateApi';
import { insufficientStockMessage } from '../../utils/apiError';

const fmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;

// ─── ExchangeCheckoutModal ─────────────────────────────────────────────────────
// Two-phase flow:
//   Phase 'confirm'  → shows estimated summary; button creates the draft
//   Phase 'payment'  → refund-method picker, only for customer_refund + cashier_decides
// Even exchanges and customer_pays create + complete in one shot here — a
// customer_pays exchange never collects payment in this modal at all: the
// return's value is credited as store credit and the new items become a
// draft order the cashier finishes at the normal checkout screen (Coupon,
// Loyalty, Store Credit, split payment — all already there), via
// onHandoffToCheckout. See ExchangeCompletionService#hand_off_to_checkout!.

export default function ExchangeCheckoutModal({
  open,
  onClose,
  onSuccess,            // called after an even/refund exchange completes here
  onHandoffToCheckout,  // called with the pending order id for customer_pays
  sourceOrder,
  returnItemsPayload,
  newItemsPayload,
  returnTotal,
  newTotal,
  orgSettings,
}) {
  const navigate = useNavigate();
  const showToast = useToast();
  const { isConnected, silentPrintExchange } = usePrinterContext();
  const { data: receiptTemplate } = useGetReceiptTemplateQuery();

  // Frontend estimate (used in 'confirm' phase summary only)
  const estNet = newTotal - returnTotal;
  const estDirection =
    estNet > 0.005 ? 'customer_pays' :
    estNet < -0.005 ? 'customer_refund' : 'even';
  const estAbsNet = Math.abs(estNet);

  // Local state
  const [phase, setPhase] = useState('confirm'); // 'confirm' | 'payment' | 'success'
  const [draftExchange, setDraftExchange] = useState(null);
  const [completedExchange, setCompletedExchange] = useState(null);
  const [refundMethod, setRefundMethod] = useState('cash');
  const [error, setError] = useState('');

  // Mutations
  const [createExchange, { isLoading: isCreating }] = useCreateExchangeMutation();
  const [completeExchange, { isLoading: isCompleting }] = useCompleteExchangeMutation();
  const isSubmitting = isCreating || isCompleting;

  // Backend-verified amounts (used in 'payment'/'success' phases)
  const backendNet = parseFloat(draftExchange?.net_settlement_amount || 0);
  const backendDir = draftExchange?.settlement_direction || estDirection;
  const backendAbsNet = Math.abs(backendNet);
  // BXGY promo discount reversed on kept items because the return side breaks
  // the promo condition — already deducted from the backend's return credit
  // and net settlement; surfaced here so the cashier can explain the figure.
  const draftClawback = parseFloat(draftExchange?.return_side?.promotion_clawback_total || 0);
  const draftClawbackName = (draftExchange?.return_side?.promotion_clawback || [])[0]?.promotion_name;

  const dirColors = { customer_pays: '#1D4ED8', customer_refund: '#7C3AED', even: '#059669' };
  const dirBg    = { customer_pays: '#EFF6FF',  customer_refund: '#F5F3FF',  even: '#ECFDF5' };
  const dirLabel = { customer_pays: 'CUSTOMER PAYS', customer_refund: 'CUSTOMER REFUND', even: 'EVEN EXCHANGE' };

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  const resetLocal = () => {
    setPhase('confirm');
    setDraftExchange(null);
    setCompletedExchange(null);
    setRefundMethod('cash');
    setError('');
  };

  const handleClose = () => { resetLocal(); onClose(); };
  const handleSuccess = () => { resetLocal(); onSuccess?.(); };

  // ─── Phase: confirm → create draft ───────────────────────────────────────────

  const handleCreateDraft = async () => {
    setError('');
    try {
      const createRes = await createExchange({
        orderId: sourceOrder.id,
        return_items: returnItemsPayload,
        new_items: newItemsPayload,
      }).unwrap();
      const exc = createRes?.data?.exchange || createRes?.exchange;
      if (!exc?.id) throw new Error('Exchange creation failed');
      setDraftExchange(exc);

      const dir = exc.settlement_direction;

      if (dir === 'even') {
        // Auto-complete even exchanges
        const completeRes = await completeExchange({ orderId: sourceOrder.id, id: exc.id, payments: [] }).unwrap();
        const completed = completeRes?.data?.exchange || completeRes?.exchange;
        setCompletedExchange(completed || exc);
        setPhase('success');
        return;
      }

      if (dir === 'customer_pays') {
        // Nothing to collect here — the backend credits the return's value as
        // store credit and hands back a draft order for the new items; the
        // cashier pays the difference through the normal checkout screen.
        const completeRes = await completeExchange({ orderId: sourceOrder.id, id: exc.id, payments: [] }).unwrap();
        const pendingOrderId = completeRes?.data?.pending_order_id || completeRes?.pending_order_id;
        if (!pendingOrderId) throw new Error('Exchange completed without a payable order.');
        resetLocal();
        onHandoffToCheckout?.(pendingOrderId);
        return;
      }

      if (dir === 'customer_refund') {
        const refundPolicy = orgSettings?.return_refund_policy;
        if (refundPolicy && refundPolicy !== 'cashier_decides') {
          // Auto-complete with fixed policy
          const completeRes = await completeExchange({
            orderId: sourceOrder.id,
            id: exc.id,
            payments: [],
            refund_method: refundPolicy,
          }).unwrap();
          const completed = completeRes?.data?.exchange || completeRes?.exchange;
          setCompletedExchange(completed || exc);
          setPhase('success');
          return;
        }
      }

      // customer_refund with cashier_decides → go to payment phase
      setPhase('payment');
    } catch (err) {
      const msg = insufficientStockMessage(err) || err?.data?.error || err?.data?.errors?.join(', ') || err?.error || 'Failed to create exchange.';
      setError(msg);
    }
  };

  // ─── Phase: payment → complete (customer_refund only) ────────────────────────

  const handleCompletePayment = async () => {
    setError('');

    if (!refundMethod) {
      showToast('Select a refund method', 'error');
      return;
    }

    try {
      const completeRes = await completeExchange({
        orderId: sourceOrder.id, id: draftExchange.id, refund_method: refundMethod,
      }).unwrap();
      const completed = completeRes?.data?.exchange || completeRes?.exchange;
      setCompletedExchange(completed || draftExchange);
      setPhase('success');
    } catch (err) {
      // Promotion clawback drifted since the draft (another return/exchange
      // completed in between). Backend refreshed the draft — reload figures
      // and ask the cashier to re-confirm.
      if (err?.data?.details?.code === 'clawback_recalculated' && err?.data?.details?.exchange) {
        setDraftExchange(err.data.details.exchange);
        setError('Settlement amount was recalculated — please review and confirm again.');
        return;
      }
      const msg = err?.data?.error || err?.data?.errors?.join(', ') || err?.error || 'Exchange failed. Please try again.';
      setError(msg);
    }
  };

  // ─── Success state ────────────────────────────────────────────────────────────

  if (phase === 'success' && completedExchange) {
    const orderId = sourceOrder?.id;
    const exchangeId = completedExchange?.id;

    return (
      <Dialog open={open} onClose={handleSuccess} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', m: { xs: 2, sm: 3 } } }}>
        <DialogContent sx={{ px: 3, py: 3, textAlign: 'center' }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <CheckCircle sx={{ fontSize: 38, color: '#10B981' }} />
          </Box>
          <Typography fontWeight={900} fontSize="1.1rem" color="#1E293B" sx={{ mb: 0.5 }}>Exchange Complete</Typography>
          <Typography fontSize="0.8rem" color="#64748B" sx={{ mb: 2 }}>
            {sourceOrder?.order_number} has been exchanged successfully
          </Typography>

          <Box sx={{ bgcolor: '#F8FAFC', borderRadius: '12px', p: 2, mb: 2, textAlign: 'left' }}>
            {completedExchange?.return_side?.credit_note_number && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography fontSize="0.75rem" color="#64748B">Credit Note</Typography>
                <Typography fontSize="0.75rem" fontWeight={700} color="#7C3AED">{completedExchange.return_side.credit_note_number}</Typography>
              </Box>
            )}
            {completedExchange?.new_sale_side?.order_number && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography fontSize="0.75rem" color="#64748B">New Invoice</Typography>
                <Typography fontSize="0.75rem" fontWeight={700} color="#4F46E5">{completedExchange.new_sale_side.order_number}</Typography>
              </Box>
            )}
            {parseFloat(completedExchange?.return_side?.promotion_clawback_total || 0) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography fontSize="0.75rem" color="#C2410C">Promotion Reversed</Typography>
                <Typography fontSize="0.75rem" fontWeight={700} color="#C2410C">
                  −{fmt(parseFloat(completedExchange.return_side.promotion_clawback_total))}
                </Typography>
              </Box>
            )}
            {completedExchange?.return_side?.debit_note_number && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography fontSize="0.75rem" color="#64748B">Debit Note</Typography>
                <Typography fontSize="0.75rem" fontWeight={700} color="#C2410C">{completedExchange.return_side.debit_note_number}</Typography>
              </Box>
            )}
            {backendDir !== 'even' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontSize="0.75rem" color="#64748B">{backendDir === 'customer_pays' ? 'Collected' : 'Refunded'}</Typography>
                <Typography fontSize="0.75rem" fontWeight={700} color={backendDir === 'customer_pays' ? '#059669' : '#7C3AED'}>{fmt(backendAbsNet)}</Typography>
              </Box>
            )}
          </Box>

          <Stack spacing={1}>
            {completedExchange && (
              <Button fullWidth variant="outlined" startIcon={<PrintOutlined sx={{ fontSize: 15 }} />}
                onClick={async () => {
                  const receipt = mapExchangeToReceipt(completedExchange, receiptTemplate);
                  if (receipt) {
                    if (isConnected) {
                      try { await silentPrintExchange(receipt); handleSuccess(); return; } catch (err) {
                        console.error('[Printer] silentPrintExchange failed:', err);
                        showToast('Printer error — switching to browser print', 'warning');
                      }
                    } else {
                      showToast('Printer app not connected — printing via browser', 'info');
                    }
                    await new Promise((r) => setTimeout(r, 1500));
                    browserPrintExchange(receipt);
                  }
                  handleSuccess();
                }}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', color: '#4F46E5', borderColor: '#C7D2FE', '&:hover': { bgcolor: '#EEF2FF' } }}
              >
                Print Exchange Receipt
              </Button>
            )}
            <Button fullWidth variant="contained" disableElevation onClick={handleSuccess}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}
            >
              Back to Billing
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Payment phase (customer_refund + cashier_decides only) ──────────────────

  if (phase === 'payment') {
    return (
      <Dialog open={open} onClose={!isSubmitting ? handleClose : undefined} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', m: { xs: 2, sm: 3 } } }}>
        <DialogTitle sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => { setPhase('confirm'); setError(''); }} disabled={isSubmitting} sx={{ color: '#94A3B8' }}>
            <ArrowBack sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography fontWeight={800} fontSize="0.95rem" color="#1E293B" sx={{ flexGrow: 1 }}>Complete Exchange</Typography>
          <IconButton size="small" onClick={handleClose} disabled={isSubmitting} sx={{ color: '#94A3B8' }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ px: 2.5, py: 2 }}>
          {/* Backend-verified summary */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: dirBg[backendDir], borderRadius: '12px', px: 2, py: 1.5, mb: draftClawback > 0 ? 1 : 2 }}>
            <Typography fontSize="0.78rem" fontWeight={800} color={dirColors[backendDir]}>{dirLabel[backendDir]}</Typography>
            <Typography fontSize="1.1rem" fontWeight={900} color={dirColors[backendDir]}>{fmt(backendAbsNet)}</Typography>
          </Box>

          {/* Promotion clawback already reflected in the settlement above */}
          {draftClawback > 0 && (
            <Box sx={{ bgcolor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '10px', px: 2, py: 1, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography fontSize="0.72rem" fontWeight={700} color="#9A3412" sx={{ minWidth: 0 }}>
                Promotion reversed{draftClawbackName ? ` · ${draftClawbackName}` : ''} (return credit reduced)
              </Typography>
              <Typography fontSize="0.8rem" fontWeight={800} color="#C2410C" sx={{ flexShrink: 0 }}>
                −{fmt(draftClawback)}
              </Typography>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem', borderRadius: '10px' }}>{error}</Alert>}

          <Stack spacing={1}>
            <Typography fontSize="0.78rem" fontWeight={700} color="#374151">Select refund method</Typography>
            {[
              { value: 'same_payment_mode', label: 'Same Payment Mode' },
              { value: 'cash', label: 'Cash' },
              { value: 'store_credit', label: 'Store Credit' },
            ].map(({ value, label }) => (
              <Box key={value} onClick={() => setRefundMethod(value)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '10px', border: '2px solid', borderColor: refundMethod === value ? '#6366F1' : '#E2E8F0', bgcolor: refundMethod === value ? '#F5F3FF' : '#fff', cursor: 'pointer', transition: 'all 0.15s' }}>
                <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: refundMethod === value ? '#6366F1' : '#CBD5E1', bgcolor: refundMethod === value ? '#6366F1' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {refundMethod === value && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#fff' }} />}
                </Box>
                <Typography fontSize="0.85rem" fontWeight={700} color="#1E293B">{label}</Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
          <Button fullWidth variant="outlined" onClick={handleClose} disabled={isSubmitting}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', color: '#64748B', borderColor: '#E2E8F0' }}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" disableElevation onClick={handleCompletePayment} disabled={isSubmitting}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}>
            {isSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ─── Confirm phase (default) ──────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={!isSubmitting ? handleClose : undefined} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '16px', m: { xs: 2, sm: 3 } } }}>
      <DialogTitle sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={800} fontSize="0.95rem" color="#1E293B">Complete Exchange</Typography>
        <IconButton size="small" onClick={handleClose} disabled={isSubmitting} sx={{ color: '#94A3B8' }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ px: 2.5, py: 2 }}>
        {/* Estimated summary */}
        <Box sx={{ bgcolor: '#F8FAFC', borderRadius: '12px', p: 1.75, mb: 2 }}>
          <Typography fontSize="0.62rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 0.75 }}>EXCHANGE SUMMARY</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
            <Typography fontSize="0.78rem" color="#64748B">Return credit</Typography>
            <Typography fontSize="0.78rem" fontWeight={700} color="#C2410C">−{fmt(returnTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography fontSize="0.78rem" color="#64748B">New items</Typography>
            <Typography fontSize="0.78rem" fontWeight={700} color="#4F46E5">+{fmt(newTotal)}</Typography>
          </Box>
          <Divider sx={{ mb: 0.75 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: dirBg[estDirection], borderRadius: '8px', px: 1.25, py: 0.75 }}>
            <Typography fontSize="0.72rem" fontWeight={800} color={dirColors[estDirection]}>{dirLabel[estDirection]}</Typography>
            <Typography fontSize="1rem" fontWeight={900} color={dirColors[estDirection]}>
              {estDirection === 'even' ? '₹0' : fmt(estAbsNet)}
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem', borderRadius: '10px' }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button fullWidth variant="outlined" onClick={handleClose} disabled={isSubmitting}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', color: '#64748B', borderColor: '#E2E8F0' }}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" disableElevation onClick={handleCreateDraft} disabled={isSubmitting}
          sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}>
          {isSubmitting
            ? <CircularProgress size={18} sx={{ color: '#fff' }} />
            : estDirection === 'customer_pays' ? 'Checkout' : 'Complete Exchange'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
